import React, { useCallback, useEffect, useState } from 'react';
import {
  stockPortfolioService,
  T212Account,
  T212AccountTestResult,
} from '../../services/stockPortfolioService';

interface Trading212AccountsSectionProps {
  isMobile: boolean;
}

/**
 * Trading 212 account management, rendered inside My Trading Accounts when the
 * "Trading 212" provider is selected. Fully separate from the KuCoin form.
 */
const Trading212AccountsSection: React.FC<Trading212AccountsSectionProps> = ({ isMobile }) => {
  const [accounts, setAccounts] = useState<T212Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [testingId, setTestingId] = useState<number | null>(null);
  const [testResult, setTestResult] = useState<T212AccountTestResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    name: '',
    apiKey: '',
    apiSecret: '',
    environment: 'DEMO' as 'DEMO' | 'LIVE',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setAccounts(await stockPortfolioService.listT212Accounts());
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Failed to load Trading 212 accounts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.apiKey.trim()) {
      setError('Account name and API key are required');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await stockPortfolioService.createT212Account({
        name: form.name.trim(),
        apiKey: form.apiKey.trim(),
        apiSecret: form.apiSecret.trim() || undefined,
        environment: form.environment,
      });
      setForm({ name: '', apiKey: '', apiSecret: '', environment: 'DEMO' });
      await load();
    } catch (e: any) {
      setError(e?.message || 'Failed to create Trading 212 account');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTest = async (account: T212Account) => {
    setTestingId(account.id);
    setTestResult(null);
    setError(null);
    try {
      const result = await stockPortfolioService.testT212Account(account.id);
      setTestResult(result);
      setTimeout(() => setTestResult(null), 6000);
    } catch (e: any) {
      setError(e?.message || 'Connection test failed');
    } finally {
      setTestingId(null);
    }
  };

  const handleDelete = async (account: T212Account) => {
    if (!window.confirm(`Deactivate Trading 212 account "${account.name}"?`)) return;
    setError(null);
    try {
      await stockPortfolioService.deleteT212Account(account.id);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Failed to deactivate account');
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 16px',
    border: '1px solid #d1d5db',
    borderRadius: 8,
    fontSize: 14,
    outline: 'none',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: 8,
    fontSize: 14,
    fontWeight: 600,
    color: '#374151',
  };

  return (
    <div>
      {/* Info banner */}
      <div style={{
        marginBottom: isMobile ? 20 : 28,
        padding: isMobile ? '16px' : '20px',
        background: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)',
        borderRadius: 16,
        color: 'white',
      }}>
        <div style={{ fontWeight: 700, fontSize: 16 }}>Trading 212 — real stocks &amp; ETFs</div>
        <p style={{ margin: '6px 0 0', fontSize: 13, color: 'rgba(255,255,255,0.92)', lineHeight: 1.5 }}>
          Generate an API key in the Trading 212 app under <strong>Settings → API (Beta)</strong>.
          Works with Invest and Stocks ISA accounts only (CFD accounts are not supported).
          Start with a <strong>demo / practice</strong> account before connecting a live one.
        </p>
      </div>

      {/* Error */}
      {error && (
        <div style={{
          marginBottom: 20,
          padding: '14px 18px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: 12,
          color: '#991b1b',
          fontSize: 14,
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
        }}>
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            style={{ background: 'none', border: 'none', color: '#991b1b', cursor: 'pointer', fontSize: 18 }}
          >
            ×
          </button>
        </div>
      )}

      {/* Add account form */}
      <div style={{
        marginBottom: isMobile ? 20 : 28,
        backgroundColor: 'white',
        borderRadius: 16,
        border: '1px solid #e5e7eb',
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
        overflow: 'hidden',
      }}>
        <div style={{ padding: isMobile ? 16 : 24, borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
          <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#111827' }}>Connect Trading 212 Account</h3>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: '#6b7280' }}>
            One API key per Trading 212 account — the key only needs read access for the Stock Portfolio view
          </p>
        </div>
        <form onSubmit={handleSubmit} style={{ padding: isMobile ? 16 : 24 }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? 260 : 300}px, 1fr))`,
            gap: isMobile ? 16 : 20,
            marginBottom: 20,
          }}>
            <div>
              <label style={labelStyle}>Account Name <span style={{ color: '#ef4444' }}>*</span></label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="My T212 practice account"
                maxLength={100}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>API Key <span style={{ color: '#ef4444' }}>*</span></label>
              <input
                type="password"
                value={form.apiKey}
                onChange={e => setForm(prev => ({ ...prev, apiKey: e.target.value }))}
                placeholder="Paste the key from Settings → API (Beta)"
                style={{ ...inputStyle, fontFamily: 'monospace' }}
              />
            </div>
            <div>
              <label style={labelStyle}>API Secret <span style={{ color: '#9ca3af', fontWeight: 400 }}>(only if your key has one)</span></label>
              <input
                type="password"
                value={form.apiSecret}
                onChange={e => setForm(prev => ({ ...prev, apiSecret: e.target.value }))}
                placeholder="Leave blank for older single-key setups"
                style={{ ...inputStyle, fontFamily: 'monospace' }}
              />
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <span style={labelStyle}>Environment</span>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {([
                { id: 'DEMO', label: 'Demo / practice (recommended first)' },
                { id: 'LIVE', label: 'Live — real money' },
              ] as const).map(opt => (
                <label
                  key={opt.id}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '10px 16px',
                    borderRadius: 10,
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 600,
                    border: form.environment === opt.id ? '2px solid #086afb' : '1px solid #d1d5db',
                    backgroundColor: form.environment === opt.id ? '#eff6ff' : '#fff',
                    color: opt.id === 'LIVE' ? '#b91c1c' : '#374151',
                  }}
                >
                  <input
                    type="radio"
                    name="t212Environment"
                    checked={form.environment === opt.id}
                    onChange={() => setForm(prev => ({ ...prev, environment: opt.id }))}
                    style={{ accentColor: '#086afb' }}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: isMobile ? 'stretch' : 'flex-end' }}>
            <button
              type="submit"
              disabled={submitting}
              style={{
                padding: '12px 24px',
                backgroundColor: '#086afb',
                border: 'none',
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 600,
                color: 'white',
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.6 : 1,
                width: isMobile ? '100%' : 'auto',
              }}
            >
              {submitting ? 'Connecting…' : 'Connect Account'}
            </button>
          </div>
        </form>
      </div>

      {/* Test result banner */}
      {testResult && (
        <div style={{
          marginBottom: 20,
          padding: '14px 18px',
          backgroundColor: testResult.success ? '#f0fdf4' : '#fef2f2',
          border: `1px solid ${testResult.success ? '#bbf7d0' : '#fecaca'}`,
          borderRadius: 12,
          color: testResult.success ? '#166534' : '#991b1b',
          fontSize: 14,
          fontWeight: 500,
        }}>
          {testResult.success ? '✓ ' : '⚠️ '}{testResult.message} ({testResult.responseTimeMs} ms)
          {testResult.error ? ` — ${testResult.error}` : ''}
        </div>
      )}

      {/* Accounts list */}
      {loading ? (
        <p style={{ fontSize: 14, color: '#6b7280' }}>Loading Trading 212 accounts…</p>
      ) : accounts.length === 0 ? (
        <div style={{
          padding: isMobile ? '32px 20px' : '48px 40px',
          backgroundColor: 'white',
          borderRadius: 16,
          border: '2px dashed #d1d5db',
          textAlign: 'center',
        }}>
          <h3 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, color: '#111827' }}>
            No Trading 212 accounts
          </h3>
          <p style={{ margin: 0, fontSize: 14, color: '#6b7280' }}>
            Connect one with the form above, then open the <strong>Stock Portfolio</strong> tab to see your holdings.
          </p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(340px, 1fr))',
          gap: 16,
        }}>
          {accounts.map(account => (
            <div
              key={account.id}
              style={{
                backgroundColor: 'white',
                borderRadius: 16,
                border: '1px solid #e5e7eb',
                padding: isMobile ? '18px 14px' : 20,
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 12 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: '#111827' }}>{account.name}</div>
                  <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, letterSpacing: 0.5, marginTop: 2 }}>
                    TRADING 212
                  </div>
                </div>
                <span style={{
                  padding: '3px 10px',
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 700,
                  backgroundColor: account.environment === 'LIVE' ? '#dcfce7' : '#fef3c7',
                  color: account.environment === 'LIVE' ? '#15803d' : '#b45309',
                }}>
                  {account.environment === 'LIVE' ? 'LIVE' : 'DEMO'}
                </span>
              </div>
              <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 14 }}>
                API key {account.apiKeyConfigured ? 'configured ✓' : 'missing ✗'}
                {account.createdAt ? ` · added ${new Date(account.createdAt).toLocaleDateString()}` : ''}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => handleTest(account)}
                  disabled={testingId === account.id}
                  style={{
                    flex: 1,
                    padding: '9px 14px',
                    backgroundColor: '#f0fdf4',
                    border: '1px solid #bbf7d0',
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#166534',
                    cursor: testingId === account.id ? 'not-allowed' : 'pointer',
                    opacity: testingId === account.id ? 0.6 : 1,
                  }}
                >
                  {testingId === account.id ? 'Testing…' : 'Test connection'}
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(account)}
                  style={{
                    padding: '9px 14px',
                    backgroundColor: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#dc2626',
                    cursor: 'pointer',
                  }}
                >
                  Deactivate
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Trading212AccountsSection;
